import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { sanitizeAreaInput, extractMapSrc } from "../../utils/format";
import { resizeImage } from "../../utils/image";

// 物件種別の選択肢
const PropertyTypes = ["土地", "新築戸建", "中古戸建", "マンション", "事業用", "その他"];

// 用途地域の選択肢
const ZoningOptions = [
  "第一種低層住居専用地域",
  "第二種低層住居専用地域",
  "第一種中高層住居専用地域",
  "第二種中高層住居専用地域",
  "第一種住居地域",
  "第二種住居地域",
  "準住居地域",
  "田園住居地域",
  "近隣商業地域",
  "商業地域",
  "準工業地域",
  "工業地域",
  "工業専用地域",
  "その他",
];

// 取引形態（取引態様）の選択肢
const TransactionTypes = ["売主", "代理", "媒介", "その他"];

const RealEstateForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const initialPhotosRef = useRef([]);
  const initialDetailFilesRef = useRef([]);
  const photoKeyRef = useRef(0);
  const nextPhotoKey = () => `p${photoKeyRef.current++}`;

  const [name, setName] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [price, setPrice] = useState("");
  const [address, setAddress] = useState("");
  const [landArea, setLandArea] = useState("");
  const [buildingArea, setBuildingArea] = useState("");
  const [layout, setLayout] = useState("");
  const [buildYear, setBuildYear] = useState("");
  const [body, setBody] = useState("");
  const [chiban, setChiban] = useState("");
  const [residenceDisplay, setResidenceDisplay] = useState("");
  const [transport, setTransport] = useState("");
  const [zoning, setZoning] = useState("");
  const [fireZone, setFireZone] = useState("");
  const [cityPlanRoad, setCityPlanRoad] = useState("");
  const [heightDistrict, setHeightDistrict] = useState("");
  const [roadWidth, setRoadWidth] = useState("");
  const [roadAccess, setRoadAccess] = useState("");
  const [landCategory, setLandCategory] = useState("");
  const [buildingCoverage, setBuildingCoverage] = useState("");
  const [floorAreaRatio, setFloorAreaRatio] = useState("");
  const [units, setUnits] = useState("");
  const [structure, setStructure] = useState("");
  const [surfaceYield, setSurfaceYield] = useState("");
  const [minpakuOccupancy, setMinpakuOccupancy] = useState("");
  const [equipment, setEquipment] = useState("");
  const [otherRestrictions, setOtherRestrictions] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [handoverDate, setHandoverDate] = useState("");
  const [taxIncluded, setTaxIncluded] = useState(true);
  const [photos, setPhotos] = useState([]); // [{ key, kind:'existing'|'new', url, file? }]
  const [inquiryUrl, setInquiryUrl] = useState("");
  const [mapUrl, setMapUrl] = useState("");
  const [detailFiles, setDetailFiles] = useState([]); // 既存PDF [{ name, url }]
  const [newPdfs, setNewPdfs] = useState([]); // 新規PDF File[]
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 詳細取得
  useEffect(() => {
    if (!isEdit) return;
    const token = localStorage.getItem("admin_jwt");
    fetch(`/api/realestate?id=${encodeURIComponent(id)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((r) => {
        if (!r.ok) throw new Error("取得失敗");
        return r.json();
      })
      .then((data) => {
        setName(data.name || "");
        setPropertyType(data.propertyType || "");
        setPrice(data.price || "");
        setAddress(data.address || "");
        setLandArea(sanitizeAreaInput(data.landArea || ""));
        setBuildingArea(sanitizeAreaInput(data.buildingArea || ""));
        setLayout(data.layout || "");
        setBuildYear(data.buildYear || "");
        setBody(data.body || "");
        setChiban(data.chiban || "");
        setResidenceDisplay(data.residenceDisplay || "");
        setTransport(data.transport || "");
        setZoning(data.zoning || "");
        setFireZone(data.fireZone || "");
        setCityPlanRoad(data.cityPlanRoad || "");
        setHeightDistrict(data.heightDistrict || "");
        setRoadWidth(sanitizeAreaInput(data.roadWidth || ""));
        setRoadAccess(data.roadAccess || "");
        setLandCategory(data.landCategory || "");
        setBuildingCoverage(sanitizeAreaInput(data.buildingCoverage || ""));
        setFloorAreaRatio(sanitizeAreaInput(data.floorAreaRatio || ""));
        setUnits(data.units || "");
        setStructure(data.structure || "");
        setSurfaceYield(sanitizeAreaInput(data.surfaceYield || ""));
        setMinpakuOccupancy(data.minpakuOccupancy || "");
        setEquipment(data.equipment || "");
        setOtherRestrictions(data.otherRestrictions || "");
        setTransactionType(data.transactionType || "");
        setHandoverDate(data.handoverDate || "");
        setTaxIncluded(data.taxIncluded !== false);
        const loadedPhotos = Array.isArray(data.photos) ? data.photos : [];
        setPhotos(
          loadedPhotos.map((url) => ({
            key: nextPhotoKey(),
            kind: "existing",
            url,
          }))
        );
        initialPhotosRef.current = loadedPhotos;
        setInquiryUrl(typeof data.inquiryUrl === "string" ? data.inquiryUrl : "");
        setMapUrl(typeof data.mapUrl === "string" ? data.mapUrl : "");
        setDetailFiles(Array.isArray(data.detailFiles) ? data.detailFiles : []);
        initialDetailFilesRef.current = Array.isArray(data.detailFiles)
          ? data.detailFiles
          : [];
      })
      .catch(console.error);
  }, [id, isEdit]);

  // 新規ファイル選択（推奨値へ自動リサイズ・圧縮。写真リスト末尾に追加）
  const onSelectFiles = async (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = ""; // 同じファイルを選び直せるように
    const resized = await Promise.all(picked.map((f) => resizeImage(f)));
    setPhotos((prev) => [
      ...prev,
      ...resized.map((f) => ({
        key: nextPhotoKey(),
        kind: "new",
        url: URL.createObjectURL(f),
        file: f,
      })),
    ]);
  };

  // 写真の削除（新規はobjectURLを解放）
  const handleRemovePhoto = (key) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.key === key);
      if (target?.kind === "new" && target.url) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.key !== key);
    });
  };

  // 写真の並び替え（dir = -1:前へ / +1:後ろへ）
  const movePhoto = (index, dir) => {
    setPhotos((prev) => {
      const to = index + dir;
      if (to < 0 || to >= prev.length) return prev;
      const arr = [...prev];
      const [moved] = arr.splice(index, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  // PDF（詳細資料）選択
  const MaxPdfBytes = 4 * 1024 * 1024; // 約4MB（Vercel関数の上限対策）
  const onSelectPdfs = (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = "";
    const valid = [];
    for (const f of picked) {
      if (f.size > MaxPdfBytes) {
        alert(`「${f.name}」は4MBを超えています。4MB以下のPDFを添付してください。`);
        continue;
      }
      valid.push(f);
    }
    setNewPdfs((prev) => [...prev, ...valid]);
  };

  // 既存PDF削除（ローカル除外・保存時に反映）
  const handleRemoveExistingPdf = (url) => {
    setDetailFiles((prev) => prev.filter((f) => f.url !== url));
  };

  // 新規（未保存）PDFの取り消し
  const handleRemoveNewPdf = (index) => {
    setNewPdfs((prev) => prev.filter((_, i) => i !== index));
  };

  // 保存処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // 二重送信ガード
    setIsSubmitting(true);
    const token = localStorage.getItem("admin_jwt");

    try {
    // 写真：並び順を保持して finalPhotos を構築（新規はアップロード）
    const finalPhotos = [];
    for (const p of photos) {
      if (p.kind === "existing") {
        finalPhotos.push(p.url);
        continue;
      }
      const fd = new FormData();
      fd.append("file", p.file);
      const res = await fetch("/api/blob/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        alert("画像アップロードに失敗しました");
        return;
      }
      const json = await res.json();
      finalPhotos.push(json.url);
    }

    // PDF（詳細資料）アップロード
    const uploadedPdfs = [];
    for (let file of newPdfs) {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/blob/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        alert("PDFのアップロードに失敗しました");
        return;
      }
      const json = await res.json();
      uploadedPdfs.push({ name: file.name, url: json.url });
    }
    const finalDetailFiles = [...detailFiles, ...uploadedPdfs];

    // ★ 削除対象ファイル（編集時のみ：写真＋PDF）
    if (isEdit) {
      const deletedPhotos = initialPhotosRef.current.filter(
        (url) => !finalPhotos.includes(url)
      );
      const deletedPdfs = initialDetailFilesRef.current
        .map((f) => f && f.url)
        .filter(Boolean)
        .filter((url) => !finalDetailFiles.some((f) => f.url === url));
      const deleted = [...deletedPhotos, ...deletedPdfs];
      for (let url of deleted) {
        try {
          await fetch("/api/blob/delete", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ url }),
          });
        } catch (err) {
          console.warn("Blob削除APIエラー:", err);
        }
      }
    }

    // 本体保存API
    const payload = {
      id: isEdit ? id : undefined,
      name,
      propertyType,
      price,
      address,
      landArea,
      buildingArea,
      layout,
      buildYear,
      chiban,
      residenceDisplay,
      transport,
      zoning,
      fireZone,
      cityPlanRoad,
      heightDistrict,
      roadWidth,
      roadAccess,
      landCategory,
      buildingCoverage,
      floorAreaRatio,
      units,
      structure,
      surfaceYield,
      minpakuOccupancy,
      equipment,
      otherRestrictions,
      transactionType,
      handoverDate,
      taxIncluded,
      body,
      photos: finalPhotos,
      detailFiles: finalDetailFiles,
      inquiryUrl,
      mapUrl: extractMapSrc(mapUrl),
    };

    const apiRes = await fetch("/api/realestate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (apiRes.ok) {
      photos.forEach(
        (p) => p.kind === "new" && p.url && URL.revokeObjectURL(p.url)
      );
      alert("保存しました");
      nav("/admin/realestate");
    } else {
      alert("保存に失敗しました");
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="adm-title mb-4">
        {isEdit ? "売買物件編集" : "売買物件追加"}
      </h1>
      <form onSubmit={handleSubmit} className="adm-form adm-card space-y-4">
        {/* 物件名 */}
        <div>
          <label>物件名</label>
          <input
            className="adm-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        {/* 販売価格 */}
        <div>
          <label>販売価格</label>
          <input
            className="adm-input"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="例：29800000（円単位の数字） / 応相談"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            数字のみ入力すると「29,800,000円（税込）」の形式で表示されます。「応相談」などの文字はそのまま表示されます。
          </p>
          <div className="mt-2">
            <label>税込表示</label>
            <select
              className="adm-input"
              value={taxIncluded ? "true" : "false"}
              onChange={(e) => setTaxIncluded(e.target.value === "true")}
            >
              <option value="true">税込で表示する</option>
              <option value="false">税込を表示しない（非課税など）</option>
            </select>
          </div>
        </div>
        {/* 表面利回り・民泊稼働率 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>表面利回り（%）</label>
            <input
              className="adm-input"
              value={surfaceYield}
              onChange={(e) => setSurfaceYield(sanitizeAreaInput(e.target.value))}
              inputMode="decimal"
              placeholder="例：7"
            />
          </div>
          <div className="flex-1">
            <label>民泊稼働率</label>
            <input
              className="adm-input"
              value={minpakuOccupancy}
              onChange={(e) => setMinpakuOccupancy(e.target.value)}
              placeholder="例：稼働率70%"
            />
          </div>
        </div>
        {/* 物件種別 */}
        <div>
          <label>物件種別</label>
          <select
            className="adm-input"
            value={propertyType}
            onChange={(e) => setPropertyType(e.target.value)}
          >
            <option value="">選択してください</option>
            {PropertyTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {/* 所在地 */}
        <div>
          <label>所在地</label>
          <input
            className="adm-input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="例：山梨県富士吉田市○○"
          />
        </div>
        {/* 地番・住居表示 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>地番</label>
            <input
              className="adm-input"
              value={chiban}
              onChange={(e) => setChiban(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label>住居表示</label>
            <input
              className="adm-input"
              value={residenceDisplay}
              onChange={(e) => setResidenceDisplay(e.target.value)}
            />
          </div>
        </div>
        {/* 交通 */}
        <div>
          <label>交通</label>
          <input
            className="adm-input"
            value={transport}
            onChange={(e) => setTransport(e.target.value)}
            placeholder="例：富士急行線 富士山駅 徒歩10分"
          />
        </div>
        {/* 土地面積・建物面積 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>土地面積（㎡）</label>
            <input
              className="adm-input"
              value={landArea}
              onChange={(e) => setLandArea(sanitizeAreaInput(e.target.value))}
              inputMode="decimal"
              placeholder="例：120.5"
            />
          </div>
          <div className="flex-1">
            <label>建物面積（㎡）</label>
            <input
              className="adm-input"
              value={buildingArea}
              onChange={(e) => setBuildingArea(sanitizeAreaInput(e.target.value))}
              inputMode="decimal"
              placeholder="例：95.2"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 -mt-2">
          面積は半角数字のみ入力してください。㎡・坪数（約○坪）は自動で表示されます。
        </p>
        {/* 間取り・築年 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>間取り</label>
            <input
              className="adm-input"
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
              placeholder="例：3LDK"
            />
          </div>
          <div className="flex-1">
            <label>築年</label>
            <input
              className="adm-input"
              value={buildYear}
              onChange={(e) => setBuildYear(e.target.value)}
              placeholder="例：2015年3月 / 築8年"
            />
          </div>
        </div>
        {/* 戸数・構造 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>戸数</label>
            <input
              className="adm-input"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              placeholder="例：全12戸"
            />
          </div>
          <div className="flex-1">
            <label>構造</label>
            <input
              className="adm-input"
              value={structure}
              onChange={(e) => setStructure(e.target.value)}
              placeholder="例：木造2階建 / RC造"
            />
          </div>
        </div>
        {/* 設備 */}
        <div>
          <label>設備</label>
          <input
            className="adm-input"
            value={equipment}
            onChange={(e) => setEquipment(e.target.value)}
            placeholder="例：プロパンガス、浄化槽"
          />
        </div>
        {/* 地目/権利 */}
        <div>
          <label>地目/権利</label>
          <input
            className="adm-input"
            value={landCategory}
            onChange={(e) => setLandCategory(e.target.value)}
            placeholder="例：宅地 / 所有権"
          />
        </div>
        {/* 用途地域 */}
        <div>
          <label>用途地域</label>
          <select
            className="adm-input"
            value={zoning}
            onChange={(e) => setZoning(e.target.value)}
          >
            <option value="">選択してください</option>
            {ZoningOptions.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
        {/* 建ぺい率・容積率 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>建ぺい率（%）</label>
            <input
              className="adm-input"
              value={buildingCoverage}
              onChange={(e) => setBuildingCoverage(sanitizeAreaInput(e.target.value))}
              inputMode="decimal"
              placeholder="例：60"
            />
          </div>
          <div className="flex-1">
            <label>容積率（%）</label>
            <input
              className="adm-input"
              value={floorAreaRatio}
              onChange={(e) => setFloorAreaRatio(sanitizeAreaInput(e.target.value))}
              inputMode="decimal"
              placeholder="例：200"
            />
          </div>
        </div>
        {/* 道路幅員 */}
        <div>
          <label>道路幅員（m）</label>
          <input
            className="adm-input"
            value={roadWidth}
            onChange={(e) => setRoadWidth(sanitizeAreaInput(e.target.value))}
            inputMode="decimal"
            placeholder="例：4"
          />
        </div>
        <p className="text-xs text-gray-500 -mt-2">
          建ぺい率・容積率・道路幅員は半角数字のみ入力してください。単位（% / m）は自動で表示されます。
        </p>
        {/* 接道状況 */}
        <div>
          <label>接道状況</label>
          <input
            className="adm-input"
            value={roadAccess}
            onChange={(e) => setRoadAccess(e.target.value)}
            placeholder="例：南側 公道6m に接道"
          />
        </div>
        {/* 防火規制・高度地区 */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label>防火規制</label>
            <input
              className="adm-input"
              value={fireZone}
              onChange={(e) => setFireZone(e.target.value)}
              placeholder="例：準防火地域"
            />
          </div>
          <div className="flex-1">
            <label>高度地区</label>
            <input
              className="adm-input"
              value={heightDistrict}
              onChange={(e) => setHeightDistrict(e.target.value)}
              placeholder="例：第2種高度地区"
            />
          </div>
        </div>
        {/* 都市計画道路 */}
        <div>
          <label>都市計画道路</label>
          <input
            className="adm-input"
            value={cityPlanRoad}
            onChange={(e) => setCityPlanRoad(e.target.value)}
            placeholder="例：なし / 計画あり"
          />
        </div>
        {/* 他の法令上の制限 */}
        <div>
          <label>他の法令上の制限</label>
          <input
            className="adm-input"
            value={otherRestrictions}
            onChange={(e) => setOtherRestrictions(e.target.value)}
          />
        </div>
        {/* 取引形態 */}
        <div>
          <label>取引形態</label>
          <select
            className="adm-input"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
          >
            <option value="">選択してください</option>
            {TransactionTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {/* 引渡日 */}
        <div>
          <label>引渡日</label>
          <input
            className="adm-input"
            value={handoverDate}
            onChange={(e) => setHandoverDate(e.target.value)}
            placeholder="例：相談 / 2026年3月末"
          />
        </div>
        {/* 物件説明 */}
        <div>
          <label>物件説明</label>
          <textarea
            className="adm-input"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        {/* 写真 */}
        <div>
          <label>写真（複数可・◀▶で並び替え／先頭がメイン）</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onSelectFiles}
            className="block mb-2"
          />
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={p.key} className="relative border rounded p-1">
                <img
                  src={p.url}
                  alt=""
                  className={`h-24 w-full object-cover ${
                    p.kind === "new" ? "opacity-90" : ""
                  }`}
                />
                {i === 0 && (
                  <span className="absolute top-1 left-1 bg-primary text-white text-[10px] px-1 rounded">
                    メイン
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(p.key)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                >
                  ×
                </button>
                <div className="flex justify-between mt-1">
                  <button
                    type="button"
                    onClick={() => movePhoto(i, -1)}
                    disabled={i === 0}
                    className="px-2 border rounded text-sm disabled:opacity-30"
                    aria-label="前へ移動"
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    onClick={() => movePhoto(i, 1)}
                    disabled={i === photos.length - 1}
                    className="px-2 border rounded text-sm disabled:opacity-30"
                    aria-label="後ろへ移動"
                  >
                    ▶
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* 詳細資料（PDF） */}
        <div>
          <label>詳細資料（PDF・複数可）</label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={onSelectPdfs}
            className="block mb-2"
          />
          <p className="text-xs text-gray-500 mb-2">
            物件チラシ等をPDFで添付できます（1ファイル4MBまで）。公開ページではファイル名リンクとして表示されます。
          </p>
          {(detailFiles.length > 0 || newPdfs.length > 0) && (
            <ul className="space-y-1">
              {detailFiles.map((f) => (
                <li key={f.url} className="flex items-center gap-2 text-sm">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {f.name || "資料.pdf"}
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingPdf(f.url)}
                    className="text-red-600 shrink-0"
                  >
                    削除
                  </button>
                </li>
              ))}
              {newPdfs.map((f, i) => (
                <li
                  key={`newpdf-${i}`}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <span className="break-all">{f.name}（未保存）</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveNewPdf(i)}
                    className="text-red-600 shrink-0"
                  >
                    取り消し
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* お問い合わせリンク */}
        <div>
          <label>お問い合わせリンク（任意／未入力ならお問合せページ）</label>
          <input
            className="adm-input"
            value={inquiryUrl}
            onChange={(e) => setInquiryUrl(e.target.value)}
            placeholder="https://... （空欄で /contact へ）"
          />
        </div>
        {/* マップ埋め込みURL */}
        <div>
          <label>マップ埋め込みURL</label>
          <input
            className="adm-input"
            value={mapUrl}
            onChange={(e) => setMapUrl(e.target.value)}
            placeholder="https://www.google.com/maps/..."
          />
        </div>
        {/* 保存／キャンセル */}
        <div className="flex gap-2">
          <button type="submit" disabled={isSubmitting} className="adm-btn">
            {isSubmitting ? "保存中…" : "保存"}
          </button>
          <button
            type="button"
            onClick={() => nav("/admin/realestate")}
            className="adm-btn-sub"
          >
            キャンセル
          </button>
        </div>
      </form>
    </div>
  );
};

export default RealEstateForm;
